import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/AuthContext';
import {
  ArrowLeft, MapPin, Users, Calendar, DollarSign,
  Briefcase, CheckCircle2, Clock, Globe, XCircle,
  Plus, Edit2, ChevronDown, FileText, Upload,
  AlertTriangle, Image as ImageIcon, Megaphone
} from 'lucide-react';
import { format } from 'date-fns';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
let DefaultIcon = L.icon({ iconUrl: icon, shadowUrl: iconShadow, iconSize: [25, 41], iconAnchor: [12, 41] });
L.Marker.prototype.options.icon = DefaultIcon;

type Tab = 'applicants' | 'map' | 'jobs' | 'interviews';

export default function CampaignDetails() {
  const { activeUserId } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<any>(null);
  const [applications, setApplications] = useState<any[]>([]);
  const [interviews, setInterviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>('applicants');

  useEffect(() => {
    if (id) fetchData();
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch campaign with jobs and interviews (same as mobile app)
      const { data: campData, error: campErr } = await supabase
        .from('campaigns')
        .select('*, jobs(*), interviews(*)')
        .eq('id', id)
        .single();
      if (campErr) throw campErr;
      setCampaign(campData);
      setInterviews(campData.interviews || []);

      const jobIds = campData.jobs ? campData.jobs.map((j: any) => j.id) : [];
      if (jobIds.length === 0) { setApplications([]); return; }

      // Fetch applications with candidates (same as mobile app)
      const { data: appData, error: appErr } = await supabase
        .from('applications')
        .select(`*, candidate:candidates(*), job:jobs(id, role)`)
        .in('job_id', jobIds)
        .order('created_at', { ascending: false });
      if (appErr) throw appErr;
      setApplications(appData || []);
    } catch (err) {
      console.error('Failed to load campaign details:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': case 'shortlisted': case 'selected': return 'bg-green-100 text-green-700 border-green-200';
      case 'draft': return 'bg-gray-100 text-gray-700 border-gray-200';
      case 'closed': case 'rejected': case 'cancelled': return 'bg-red-100 text-red-700 border-red-200';
      case 'accepted': case 'interview_scheduled': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'completed': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'scheduled': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'selection_done': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-blue-100 text-blue-700 border-blue-200';
    }
  };

  const handleInterviewStatus = async (interviewId: string, status: 'completed' | 'cancelled') => {
    if (!window.confirm(`Mark interview as ${status}?`)) return;
    await supabase.from('interviews').update({ status }).eq('id', interviewId);
    fetchData();
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center p-12">
        <div className="animate-spin w-12 h-12 border-4 border-jobisa-yellow border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="p-8 text-center text-gray-500">
        <h2 className="text-2xl font-bold font-display text-jobisa-navy">Campaign Not Found</h2>
        <button onClick={() => navigate('/app/campaigns')} className="text-jobisa-navy mt-4 underline">Return to Campaigns</button>
      </div>
    );
  }

  const jobs = campaign.jobs || [];
  const mapCenter = applications.find(a => a.candidate?.latitude && a.candidate?.longitude)?.candidate
    ? [applications.find(a => a.candidate?.latitude)?.candidate.latitude, applications.find(a => a.candidate?.latitude)?.candidate.longitude]
    : [25.2048, 55.2708];

  const tabs: { key: Tab; label: string; count?: number }[] = [
    { key: 'applicants', label: 'Candidates', count: applications.length },
    { key: 'jobs', label: 'Jobs', count: jobs.length },
    { key: 'interviews', label: 'Interviews', count: interviews.length },
    { key: 'map', label: 'Map View' },
  ];

  return (
    <div className="max-w-7xl mx-auto pb-12 animate-in fade-in slide-in-from-bottom-4 duration-500">

      {/* === HEADER === */}
      <div className="relative overflow-hidden rounded-3xl bg-jobisa-navy p-8 md:p-10 mb-8 shadow-2xl border border-white/10 group">
        <div className="absolute top-0 right-0 w-96 h-96 bg-jobisa-yellow/10 rounded-full blur-3xl -mr-20 -mt-20 mix-blend-screen"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl -ml-20 -mb-20 mix-blend-screen"></div>

        <div className="relative z-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="flex-1 text-white">
            <button onClick={() => navigate('/app/campaigns')} className="flex items-center gap-2 text-gray-300 hover:text-white mb-6 bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-full text-sm font-medium transition-colors w-fit">
              <ArrowLeft size={16} /> Back to Campaigns
            </button>
            <div className="flex items-center gap-4 mb-3">
              <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border ${getStatusColor(campaign.status)}`}>
                {campaign.status}
              </span>
              <span className="text-jobisa-yellow/80 text-sm font-medium flex items-center gap-1.5">
                <Calendar size={14} /> Created {format(new Date(campaign.created_at), 'PPP')}
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-2 tracking-tight">
              {campaign.title}
            </h1>
            <p className="text-xl text-gray-300 font-medium flex items-center gap-2">
              <Briefcase size={20} className="text-jobisa-yellow" /> {campaign.company_name} · {campaign.country}
            </p>
          </div>

          <div className="flex gap-4 p-4 rounded-2xl bg-white/10 backdrop-blur-md border border-white/10 shadow-xl shrink-0">
            <div className="text-center px-4 border-r border-white/10">
              <p className="text-gray-400 text-xs font-bold uppercase tracking-wide mb-1">Jobs</p>
              <p className="text-2xl font-bold text-white">{jobs.length}</p>
            </div>
            <div className="text-center px-4 border-r border-white/10">
              <p className="text-gray-400 text-xs font-bold uppercase tracking-wide mb-1">Applicants</p>
              <p className="text-2xl font-bold text-jobisa-yellow">{applications.length}</p>
            </div>
            <div className="text-center px-4">
              <p className="text-gray-400 text-xs font-bold uppercase tracking-wide mb-1">Interviews</p>
              <p className="text-2xl font-bold text-white">{interviews.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* === MEA STATUS BANNER === */}
      {(() => {
        const meaStatus = campaign.mea_status;
        if (!meaStatus || meaStatus === 'not_submitted') {
          return (
            <div className="mb-6 bg-gray-50 border border-gray-200 rounded-2xl p-5 flex items-center gap-4">
              <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center"><FileText size={20} className="text-gray-500" /></div>
              <div className="flex-1">
                <p className="font-bold text-gray-800">MEA Letter Not Submitted</p>
                <p className="text-sm text-gray-500">Upload an MEA verification letter to activate this campaign.</p>
              </div>
              <button className="px-4 py-2 bg-jobisa-navy text-white text-sm font-bold rounded-xl flex items-center gap-2 hover:bg-jobisa-navy/90 transition-colors">
                <Upload size={16} /> Upload
              </button>
            </div>
          );
        }
        if (meaStatus === 'verified') {
          return (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-2xl p-5 flex items-center gap-4">
              <div className="w-10 h-10 bg-green-200 rounded-full flex items-center justify-center"><CheckCircle2 size={20} className="text-green-700" /></div>
              <div className="flex-1">
                <p className="font-bold text-green-800">MEA Letter Verified ✓</p>
                <p className="text-sm text-green-600">Document has been verified and approved.</p>
              </div>
            </div>
          );
        }
        if (meaStatus === 'pending') {
          return (
            <div className="mb-6 bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-center gap-4">
              <div className="w-10 h-10 bg-amber-200 rounded-full flex items-center justify-center"><AlertTriangle size={20} className="text-amber-700" /></div>
              <div><p className="font-bold text-amber-800">MEA Letter Under Review</p><p className="text-sm text-amber-600">The document is being reviewed by admin.</p></div>
            </div>
          );
        }
        return (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-2xl p-5 flex items-center gap-4">
            <div className="w-10 h-10 bg-red-200 rounded-full flex items-center justify-center"><XCircle size={20} className="text-red-700" /></div>
            <div className="flex-1"><p className="font-bold text-red-800">MEA Letter Rejected</p><p className="text-sm text-red-600">Please re-upload with a valid document.</p></div>
            <button className="px-4 py-2 bg-jobisa-navy text-white text-sm font-bold rounded-xl flex items-center gap-2 hover:bg-jobisa-navy/90 transition-colors"><Upload size={16} /> Re-upload</button>
          </div>
        );
      })()}

      {/* === POSTERS === */}
      {campaign.poster_urls && campaign.poster_urls.length > 0 && (
        <div className="mb-6 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2"><ImageIcon size={16} /> Campaign Posters ({campaign.poster_urls.length}/3)</h3>
          <div className="flex gap-3 overflow-x-auto">
            {campaign.poster_urls.map((url: string, i: number) => (
              <img key={i} src={url} alt={`Poster ${i + 1}`} className="w-32 h-40 object-cover rounded-xl border border-gray-200 shadow-sm" />
            ))}
          </div>
        </div>
      )}

      {/* === TABS === */}
      <div className="flex flex-col xl:flex-row gap-8">
        {/* Left Summary */}
        <div className="xl:w-80 space-y-6 shrink-0">
          <div className="bg-white rounded-3xl p-6 shadow-xl shadow-gray-200/50 border border-gray-100">
            <h3 className="text-lg font-bold font-display text-gray-900 mb-6 flex items-center gap-2">
              <CheckCircle2 size={18} className="text-green-500" /> Campaign Summary
            </h3>
            <div className="space-y-5">
              <div className="flex gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center shrink-0"><MapPin size={18} className="text-blue-600" /></div>
                <div><p className="text-sm font-semibold text-gray-800">Location</p><p className="text-sm text-gray-500">{campaign.country || campaign.address || 'Not specified'}</p></div>
              </div>
              <div className="flex gap-3">
                <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center shrink-0"><DollarSign size={18} className="text-green-600" /></div>
                <div><p className="text-sm font-semibold text-gray-800">Compensation</p><p className="text-sm text-gray-500">{campaign.salary_min && campaign.salary_max ? `${campaign.salary_min} - ${campaign.salary_max} ${campaign.salary_currency || ''}` : 'As per job roles'}</p></div>
              </div>
              <div className="flex gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center shrink-0"><Globe size={18} className="text-purple-600" /></div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Target Regions</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {campaign.target_locations && campaign.target_locations.length > 0
                      ? campaign.target_locations.map((loc: string) => (<span key={loc} className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-700">{loc}</span>))
                      : <span className="text-sm text-gray-500">Global</span>}
                  </div>
                </div>
              </div>
            </div>
            {campaign.description && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <h4 className="text-sm font-bold text-gray-800 mb-2">Description</h4>
                <p className="text-sm text-gray-500 leading-relaxed">{campaign.description}</p>
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          {/* Tabs */}
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-5 py-2.5 rounded-full font-bold text-sm transition-all flex items-center gap-2 ${activeTab === tab.key
                    ? 'bg-jobisa-navy text-white shadow-lg shadow-jobisa-navy/30'
                    : 'bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-800 border border-gray-200 shadow-sm'
                  }`}
              >
                {tab.label}
                {tab.count !== undefined && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${activeTab === tab.key ? 'bg-white/20' : 'bg-gray-100'}`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="bg-white rounded-3xl overflow-hidden shadow-xl shadow-gray-200/50 border border-gray-100 min-h-[500px]">

            {/* === CANDIDATES TAB === */}
            {activeTab === 'applicants' && (
              applications.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                  <Users size={48} className="mb-4 text-gray-300" />
                  <p className="text-lg font-medium text-gray-500">No candidates have applied yet.</p>
                </div>
              ) : (
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50/80 border-b border-gray-100 text-gray-500 text-xs uppercase tracking-wider font-bold">
                      <th className="py-4 px-6 font-semibold">Candidate</th>
                      <th className="py-4 px-6 font-semibold">Job Role</th>
                      <th className="py-4 px-6 font-semibold">Contact</th>
                      <th className="py-4 px-6 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {applications.map(app => (
                      <tr key={app.id} className="hover:bg-blue-50/30 transition-colors group">
                        <td className="py-4 px-6">
                          <p className="font-bold text-gray-900 group-hover:text-blue-700 transition-colors">{app.candidate?.name || 'Unknown'}</p>
                          <p className="text-xs text-gray-500">{app.candidate?.city || ''} {app.candidate?.experience_years ? `· ${app.candidate.experience_years}y exp` : ''}</p>
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-700">{app.job?.role || '—'}</td>
                        <td className="py-4 px-6">
                          <p className="text-sm font-medium text-gray-700">{app.candidate?.phone}</p>
                          <p className="text-xs text-gray-400">{format(new Date(app.created_at || new Date()), 'MMM d, yyyy')}</p>
                        </td>
                        <td className="py-4 px-6">
                          <span className={`px-2.5 py-1 rounded-md text-xs font-bold uppercase border ${getStatusColor(app.stage || app.status)} inline-flex items-center gap-1`}>
                            {(app.stage || app.status || 'applied').replace('_', ' ')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            )}

            {/* === JOBS TAB === */}
            {activeTab === 'jobs' && (
              <div className="p-6 space-y-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-bold text-gray-800 text-lg">Posted Jobs</h3>
                  <Link to={`/app/campaigns/${id}/jobs/new`} className="flex items-center gap-2 bg-jobisa-navy text-white px-4 py-2 rounded-xl text-sm font-bold hover:bg-jobisa-navy/90 transition-colors">
                    <Plus size={16} /> Add Job
                  </Link>
                </div>
                {jobs.length === 0 ? (
                  <div className="flex flex-col items-center py-12 text-gray-400">
                    <Briefcase size={48} className="mb-4 text-gray-300" />
                    <p className="text-lg font-medium text-gray-500">No jobs posted yet</p>
                    <Link to={`/app/campaigns/${id}/jobs/new`} className="text-blue-600 font-bold text-sm mt-2 hover:underline">Create your first job →</Link>
                  </div>
                ) : (
                  jobs.map((job: any) => {
                    const jobApps = applications.filter(a => a.job?.id === job.id);
                    return (
                      <div key={job.id} className="border border-gray-100 rounded-2xl p-5 hover:shadow-md transition-all group">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-bold text-gray-900 text-base group-hover:text-blue-700 transition-colors">{job.role}</p>
                            <p className="text-sm text-gray-500 mt-1">
                              {job.salary_min && job.salary_max ? `${job.salary_min}–${job.salary_max} ${job.currency || 'AED'}` : 'Salary TBD'}
                              {job.vacancies ? ` · ${job.vacancies} vacancies` : ''}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-xs font-bold text-gray-500 bg-gray-100 px-3 py-1 rounded-full">{jobApps.length} applicants</span>
                            <span className={`px-2.5 py-1 rounded-md text-xs font-bold uppercase border ${getStatusColor(job.status || 'active')}`}>{job.status || 'active'}</span>
                          </div>
                        </div>
                        {job.description && <p className="text-sm text-gray-500 mt-3 line-clamp-2">{job.description}</p>}
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* === INTERVIEWS TAB === */}
            {activeTab === 'interviews' && (
              <div className="p-6 space-y-4">
                <h3 className="font-bold text-gray-800 text-lg mb-2">Scheduled Interviews</h3>
                {interviews.length === 0 ? (
                  <div className="flex flex-col items-center py-12 text-gray-400">
                    <Calendar size={48} className="mb-4 text-gray-300" />
                    <p className="text-lg font-medium text-gray-500">No interviews scheduled</p>
                    <Link to="/app/interviews" className="text-blue-600 font-bold text-sm mt-2 hover:underline">Go to Interviews page →</Link>
                  </div>
                ) : (
                  interviews.map((interview: any) => (
                    <div key={interview.id} className="border border-gray-100 rounded-2xl p-5 hover:shadow-md transition-all">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-bold text-gray-900">{interview.venue || 'TBD'}</p>
                          <p className="text-sm text-gray-500 mt-1 flex items-center gap-3">
                            {interview.date && <span className="flex items-center gap-1"><Calendar size={14} /> {format(new Date(interview.date), 'PPP')}</span>}
                            {interview.address && <span className="flex items-center gap-1"><MapPin size={14} /> {interview.address}</span>}
                          </p>
                        </div>
                        <span className={`px-2.5 py-1 rounded-md text-xs font-bold uppercase border ${getStatusColor(interview.status || 'scheduled')}`}>
                          {interview.status || 'scheduled'}
                        </span>
                      </div>
                      {interview.status === 'scheduled' && (
                        <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100">
                          <button onClick={() => handleInterviewStatus(interview.id, 'completed')} className="text-sm font-medium text-green-600 hover:bg-green-50 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5">
                            <CheckCircle2 size={14} /> Complete
                          </button>
                          <button onClick={() => handleInterviewStatus(interview.id, 'cancelled')} className="text-sm font-medium text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5">
                            <XCircle size={14} /> Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* === MAP TAB === */}
            {activeTab === 'map' && (
              <div className="h-[600px] w-full relative z-0">
                <MapContainer center={mapCenter as [number, number]} zoom={4} style={{ height: '100%', width: '100%' }} className="z-0">
                  <TileLayer
                    url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                    attribution='&copy; OpenStreetMap &copy; CARTO'
                  />
                  {applications.map(app => (
                    app.candidate?.latitude && app.candidate?.longitude ? (
                      <Marker key={app.id} position={[app.candidate.latitude, app.candidate.longitude]}>
                        <Popup className="rounded-xl overflow-hidden font-sans">
                          <div className="p-1">
                            <h3 className="font-bold text-jobisa-navy text-base mb-1">{app.candidate.name}</h3>
                            <p className="text-sm text-gray-500 mb-2">{app.candidate.phone}</p>
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${getStatusColor(app.stage || app.status)}`}>
                              {app.stage || app.status}
                            </span>
                          </div>
                        </Popup>
                      </Marker>
                    ) : null
                  ))}
                </MapContainer>
                <div className="absolute top-4 right-4 z-[400] bg-white/90 backdrop-blur-md p-4 rounded-2xl shadow-lg border border-gray-100/50">
                  <h4 className="font-bold text-jobisa-navy mb-2 flex items-center gap-2">
                    <MapPin size={16} className="text-jobisa-yellow" /> Location Overview
                  </h4>
                  <div className="text-sm text-gray-600">
                    <p>Total: <span className="font-bold text-jobisa-navy">{applications.length}</span></p>
                    <p>Mapped: <span className="font-bold text-jobisa-navy">{applications.filter(a => a.candidate?.latitude).length}</span></p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
